import { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import tw from 'twin.macro';
import { useQueryParam } from 'use-query-params';

import { Button } from '@components/Button';
import { Spacer } from '@components/Spacer';
import { Input } from '@auth_module/components/Input';

import { login, fetchUserInfo, registerDirectUser } from '@auth_module/services';
import { IRequestError } from '@configs/axios-instances';
import { useAuthContext } from '@auth_module/context/AuthContext';
import { MegaphoneIcon } from '@icons/MegaphoneIcon';
import { SounderApiIcon } from '@icons/SounderApiIcon';
import {
  REGISTRATION_CTX_KEYS,
  REGISTRATION_URL_PARAM,
  REGISTRATION_STORAGE_KEY,
  registration_contexts,
} from '@account_module/constants/registrationCtxKeys';
import { EyeIcon, EyeIconCrossed } from '@icons/EyeIcon';

const HeaderWrapper = tw.div`w-full flex flex-col items-start`;
const HeaderTitle = tw.h1`text-[24px] font-bold text-accent-2 mx-auto`;
const HeaderP = tw.p`text-center mx-auto text-[14px] font-normal text-text-and-icon-muted tracking-[0.14px]`;
const StyledP = tw.p`text-sm`;
const TermsP = tw.p`text-sm text-accent-2`;
const TermsButton = tw.span`text-accent underline text-sm cursor-pointer`;
const FormWrapper = tw.div`flex flex-col items-center shrink-0 self-stretch w-full`;
const StyledForm = tw.form`flex flex-col items-start shrink-0 self-stretch gap-[12px]`;
const FlexRow = tw.div`flex items-start gap-[12px] self-stretch`;
const CardsContainer = tw.div`flex flex-col md:flex-row gap-4`;
const IconWrapper = tw.div`w-[32px] h-[32px] m-auto bg-sec-3 rounded-[4px] p-[4px] flex items-center justify-center mb-[16px]`;
const Card = tw.div`w-full md:w-[50%] text-center px-4 py-6 border border-solid border-gray-1 cursor-pointer rounded-[6px] hover:bg-page-background`;
const CardTitle = tw.h4`font-bold text-accent-2 text-[16px] tracking-[0.16px]`;
const ClickableIconWrapper = tw.div`cursor-pointer`;

const FormTitle = (): JSX.Element => {
  const { t } = useTranslation();
  const [regCtxURLParam] = useQueryParam(REGISTRATION_URL_PARAM.CTX);

  return useMemo(() => {
    if (!regCtxURLParam) {
      return <HeaderP>{t('auth.register_direct_form.ctx_picker_title')}</HeaderP>;
    }
    return <HeaderTitle>{t('auth.register_direct_form.title')}</HeaderTitle>;
  }, [regCtxURLParam]);
};

const RegisterDirectFormHeader = () => (
  <HeaderWrapper>
    <FormTitle />
  </HeaderWrapper>
);

const RegisterDirectErrorInfo = ({ error }: { error: string }) => {
  return (
    <>
      <StyledP>
        <Trans i18nKey='auth.register_direct_form.submit_error_message' values={{ error }}>
          {''}
          <a href='/login'>Login</a>
        </Trans>
      </StyledP>
    </>
  );
};

const TermsOfService = () => {
  const { setShowTermsOfService } = useAuthContext();

  return (
    <>
      <TermsP>
        <Trans i18nKey='auth.register_direct_form.terms_of_service'>
          By accepting this invite and using Sounder&apos;s Audio Insights Service, you agree to be
          bound by the
          <TermsButton onClick={() => setShowTermsOfService(true)}>here</TermsButton>.
        </Trans>
      </TermsP>
    </>
  );
};

const RegisterContextPicker = () => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setRegCtxURLParam] = useQueryParam(REGISTRATION_URL_PARAM.CTX);

  return (
    <CardsContainer>
      <Card onClick={() => setRegCtxURLParam(REGISTRATION_CTX_KEYS.PROMOHUB)}>
        <IconWrapper>
          <MegaphoneIcon tw='stroke-text-and-icon w-[24px] h-[24px]' />
        </IconWrapper>
        <CardTitle>{t('auth.register_direct_form.context_picker.promohub.title')}</CardTitle>
        <Spacer size='xs' direction='vertical' />
        <HeaderP tw='leading-[140%]'>
          {t('auth.register_direct_form.context_picker.promohub.subtitle')}
        </HeaderP>
      </Card>
      <Card onClick={() => setRegCtxURLParam(REGISTRATION_CTX_KEYS.SELF_SERVICE_API)}>
        <IconWrapper>
          <SounderApiIcon tw='stroke-text-and-icon w-[24px] h-[24px]' />
        </IconWrapper>
        <CardTitle>{t('auth.register_direct_form.context_picker.api.title')}</CardTitle>
        <Spacer size='xs' direction='vertical' />
        <HeaderP tw='leading-[140%]'>
          {t('auth.register_direct_form.context_picker.api.subtitle')}
        </HeaderP>
      </Card>
    </CardsContainer>
  );
};

type RegisterFormData = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  name: string;
};

type RegisterFormProps = {
  handleSuccessLogin: (token: string) => void;
};

const RegisterDirectForm = ({ handleSuccessLogin }: RegisterFormProps) => {
  const { t } = useTranslation();
  const [regCtxURLParam] = useQueryParam(REGISTRATION_URL_PARAM.CTX);
  const hasValidRegCtxURLParam = useMemo(
    () => !!regCtxURLParam && !!registration_contexts.includes(regCtxURLParam as string),
    [regCtxURLParam, registration_contexts]
  );

  const registerSchema = useMemo(() => {
    return yup.object().shape({
      first_name: yup
        .string()
        .required(t('auth.register_direct_form.first_name.error_message') as string),
      last_name: yup
        .string()
        .required(t('auth.register_direct_form.last_name.error_message') as string),
      email: yup
        .string()
        .email(t('auth.register_direct_form.email.error_message_invalid') as string)
        .required(t('auth.register_direct_form.email.error_message_required') as string),
      password: yup
        .string()
        .min(8, t('auth.register_direct_form.password.error_message_min') as string)
        .max(256, t('auth.register_direct_form.password.error_message_max') as string),
      password_confirmation: yup
        .string()
        .oneOf(
          [yup.ref('password')],
          t('auth.register_direct_form.confirm_password.error_message') as string
        ),
    });
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setFocus,
  } = useForm<RegisterFormData>({ resolver: yupResolver(registerSchema) });

  const [requestError, setRequestError] = useState<string | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  const getErrorMessage = (error: IRequestError) => {
    if (typeof error.message == 'string') {
      return error.message;
    }

    if (typeof error.message !== 'string') {
      const errorKey = Object.keys(error.message)[0];
      return error.message[errorKey][0];
    }

    return 'Something went wrong';
  };

  const handlePasswordEyeIconClick = () => {
    setIsPasswordVisible(!isPasswordVisible);
    setFocus('password');
  };

  const handleConfirmPasswordEyeIconClick = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
    setFocus('password_confirmation');
  };

  const onSubmit: SubmitHandler<RegisterFormData> = async (formData) => {
    const { password, email } = formData;

    try {
      await registerDirectUser(formData);
      login({ email, password }).then(({ data }) => {
        handleSuccessLogin(data.token);
        window.localStorage.setItem(
          REGISTRATION_STORAGE_KEY.SELF_SERVICE_GREETING,
          JSON.stringify(true)
        );
        fetchUserInfo();
      });
    } catch (err: unknown) {
      setRequestError(getErrorMessage(err as IRequestError) as string);
    }
  };

  return (
    <FormWrapper>
      <RegisterDirectFormHeader />
      <Spacer direction='vertical' size='xl' />
      {!hasValidRegCtxURLParam ? (
        <RegisterContextPicker />
      ) : !requestError ? (
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <FlexRow>
            <input tw='hidden opacity-0' type='text' placeholder='name' {...register('name')} />
            <Input
              autoFocus
              tw='w-full'
              type='text'
              label={t('auth.register_direct_form.first_name.label')}
              {...register('first_name')}
              error={errors.first_name?.message}
            />

            <Input
              tw='w-full'
              type='text'
              label={t('auth.register_direct_form.last_name.label')}
              {...register('last_name')}
              error={errors.last_name?.message}
            />
          </FlexRow>

          <Input
            tw='w-full'
            type='email'
            label={t('auth.register_direct_form.email.label')}
            {...register('email')}
            error={errors.email?.message}
          />

          <Input
            type={isPasswordVisible ? 'text' : 'password'}
            label={t('auth.register_direct_form.password.label')}
            {...register('password')}
            helperText='Must have at least 8 characters'
            error={errors.password?.message}
            renderIcon={() => (
              <ClickableIconWrapper onClick={handlePasswordEyeIconClick}>
                {isPasswordVisible ? (
                  <EyeIconCrossed tw='fill-accent-2' />
                ) : (
                  <EyeIcon tw='stroke-accent-2 fill-none' />
                )}
              </ClickableIconWrapper>
            )}
          />

          <Input
            type={isConfirmPasswordVisible ? 'text' : 'password'}
            label={t('auth.register_direct_form.confirm_password.label')}
            {...register('password_confirmation')}
            error={errors.password_confirmation?.message}
            renderIcon={() => (
              <ClickableIconWrapper onClick={handleConfirmPasswordEyeIconClick}>
                {isConfirmPasswordVisible ? (
                  <EyeIconCrossed tw='fill-accent-2' />
                ) : (
                  <EyeIcon tw='stroke-accent-2 fill-none' />
                )}
              </ClickableIconWrapper>
            )}
          />

          <Button.Primary
            tw='w-full rounded h-[40px] my-[12px]'
            type='submit'
            disabled={isSubmitting}
            isLoading={isSubmitting}>
            {t('auth.register_direct_form.submit_button')}
          </Button.Primary>

          <div tw='flex justify-center w-[337px] self-center'>
            <TermsOfService />
          </div>
        </StyledForm>
      ) : (
        <RegisterDirectErrorInfo error={requestError} />
      )}
    </FormWrapper>
  );
};

export { RegisterDirectForm };
